import {
  matchPath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { ContractFormRule, ContractFormSchema } from "@factory/form/form.rule";
import { useFormDefaultContract } from "@factory/form/form-default-contract.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  useQyUpdateContract,
  useQyGetContractById,
  useQyDeleteContract,
} from "@factory/query/contract.query";
import { GetResponseContractModel } from "@factory/model/query-model-contract";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { useRef, useState } from "react";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import { useReactToPrint } from "react-to-print";

export function useEditContract() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultContract();
  const navigate = useNavigate();
  const { mapUpdateContract, mapDeleteContract } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { contractId } = useParams();
  const location = useLocation();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Print", type: "secondary", value: ActionEnum.Print },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];
  const printWaiverRef = useRef(null);
  const [signatureBlob, setSignatureBlob] = useState<string | null>(null);

  const menuItems = [
    {
      label: "Information",
      icon: "pi pi-file-edit",
      command: () => navigate(""),
    },
    {
      label: "Billing",
      icon: "pi pi-dollar",
      command: () => navigate(`billing`),
    },
    {
      label: "Payment Transaction",
      icon: "pi pi-credit-card",
      command: () => navigate(`payment`),
    },
    {
      label: "Visit",
      icon: "pi pi-calendar",
      command: () => navigate(`visit`),
    },
  ];

  // QUERY DECLARATION
  // API GETBYID Contract
  const handleGetApiSuccess = async (data: GetResponseContractModel) => {
    if (!data || data.data?.length === 0) return;

    const responseData = data.data?.[0];

    setValue("tenant", responseData?.tenant || "");
    setValue("patient", responseData?.patient || "");
    setValue("service", responseData?.service || "");
    setValue("person", responseData?.person || "");
    setValue("amount", responseData?.amount || 0);
    setValue("signature", responseData?.signature || "");
    setValue("waiver", responseData?.waiver || "");
    setValue("waiverName", responseData?.waiver_name || "");

    if (responseData?.patient) {
      const patientLabel = `${responseData?.first_name} ${responseData?.last_name}`;
      setValue("patientName", patientLabel);
    }

    if (responseData?.service) {
      const serviceLabel = responseData?.service_name;
      setValue("serviceName", serviceLabel);
    }

    if (responseData?.person) {
      const docLabel = `${responseData?.person_first_name} ${responseData?.person_last_name}`;
      setValue("personName", docLabel);
    }

    if (responseData?.signature) {
      try {
        const file = await viewAsset(responseData.signature);
        const fileBlob = URL.createObjectURL(file);
        setSignatureBlob(fileBlob);

        const signatureFile = new File([file], "signature.png", {
          type: "image/png",
        });
        setValue("file", signatureFile);
      } catch (error) {
        console.error("Failed to load signature:", error);
      }
    }
  };
  const {
    data: contractResponse,
    isLoading,
    isError: contractError,
  } = useQyGetContractById(contractId || "", handleGetApiSuccess);
  const contractData = contractResponse?.data?.[0];

  // API UPDATE Contract
  const handleUpdateApiSuccess = () => {
    showSuccess("Contract updated successfully");
  };
  const { mutate: updateContract, isLoading: isEditLoading } =
    useQyUpdateContract(handleUpdateApiSuccess);

  // API DELETE Contract
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Contract deleted successfully");
  };
  const { mutate: deleteContract } = useQyDeleteContract(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<ContractFormSchema>({
    defaultValues: getDefault(contractData),
    resolver: zodResolver(ContractFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const session = watch("session");

  const handleValidate = (form: ContractFormSchema) => {
    if (!contractData || !contractData.code) {
      throw new Error("No contract data");
    }

    const formData = mapUpdateContract(form, contractData.code);
    updateContract(formData);
  };
  const handleValidateError = (err: FieldErrors<ContractFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!contractData || !contractData.code) {
      throw new Error("No contract data found");
    }

    const form = mapDeleteContract(contractData.code);
    deleteContract(form);
  };
  const handlePrintWaiver = useReactToPrint({ contentRef: printWaiverRef });
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;

      case ActionEnum.Print:
        handlePrintWaiver();
        break;

      case ActionEnum.Pay:
        break;
    }
  };

  const pathSegments = location.pathname.split("/").filter(Boolean);
  const contractIndex = pathSegments.findIndex(
    (segment) => segment === "contract"
  );
  const isExactContractRoute =
    contractIndex !== -1 && pathSegments.length === contractIndex + 2;

  return {
    contractId,
    contractData,
    isLoading,
    contractError,
    formMethod,
    actions,
    isEditLoading,
    signatureBlob,
    printWaiverRef,
    session,
    menuItems,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
    isExactContractRoute,
  };
}
